import { useState } from "react";
import { useNavigate } from "react-router";
import { useDropzone } from "react-dropzone";
import { useSelector } from "react-redux";

import Button from "../common/Button";
import Card from "../common/Card";
import Form from "../common/Form";
import Page from "../common/Page";
import APIManager from "../../managers/APIManager";
import { faCloudUploadAlt } from "@fortawesome/free-solid-svg-icons";
import { Translation } from "react-i18next";
import { capitalize } from "../../utils/manipulateText";
import axios from "axios";

import UploadVideoAWS from "./UploadVideoAWS";

const UploadVideo = () => {
  const navigate = useNavigate();

  const videoInfrastructure = useSelector(
    (state) => state?.auth?.user?.videoInfrastructure
  );

  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({ source: "" });
  const [activeTab, setActiveTab] = useState("local");
  const [selectedFile, setSelectedFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [progress, setProgress] = useState(0);

  const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;
  const CHUNK_SIZE = 5 * 1024 * 1024;

  const onChange = (e) => {
    e.preventDefault();
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    if (activeTab === "local") {
      startUpload();
    } else {
      APIManager.izleseneUploadViaUrl(values).then(() => {
        setShowModal(true);
      });
    }
  };

  const startUpload = async () => {
    if (!selectedFile) return;
    setLoading(true);

    try {
      const response = await APIManager.izleseneInitiate({
        fileName: selectedFile.name,
        totalParts: Math.ceil(selectedFile.size / CHUNK_SIZE),
      });

      await uploadChunks(
        selectedFile,
        response.uploadId,
        response.uploadRecord.s3_key
      );
    } catch (error) {
      console.error("Upload initiation failed:", error);
      alert("Failed to start upload");
    } finally {
      setLoading(false);
    }
  };

  const uploadChunks = async (file, uploadId, s3Key) => {
    const totalParts = Math.ceil(file.size / CHUNK_SIZE);
    const parts = [];

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      try {
        const { presignedUrl } = await APIManager.izleseneGetPresignedPartUrl({
          uploadId,
          partNumber,
          key: s3Key,
        });

        const putResponse = await axios.put(presignedUrl, chunk, {
          headers: {
            "Content-Type": "application/octet-stream",
            Authorization: null,
          },
          onUploadProgress: (progressEvent) => {
            const chunkProgress = progressEvent.loaded / progressEvent.total;

            const overallProgress = Math.round(
              ((partNumber - 1 + chunkProgress) / totalParts) * 100
            );

            setProgress(overallProgress);
          },
        });

        if (putResponse.status !== 200) {
          throw new Error(`S3 upload failed for part ${partNumber}`);
        }

        let eTag = putResponse.headers.etag || putResponse.headers.etag;
        if (eTag) {
          eTag = eTag.replace(/"/g, "");
        }

        parts.push({ ETag: eTag, PartNumber: partNumber });
      } catch (error) {
        console.error(`Failed to upload part ${partNumber}:`, error);
        alert("Upload interrupted.");
        return;
      }
    }

    await completeUpload(uploadId, s3Key, parts);
  };

  const completeUpload = async (uploadId, key, parts) => {
    try {
      await APIManager.izleseneComplete({ uploadId, key, parts });
      setShowModal(true);
    } catch (error) {
      console.error("Error completing upload:", error);
      alert("Failed to complete upload.");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    navigate(`/videos`);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "video/mp4": [],
      "video/mpeg": [],
      "video/quicktime": [],
      "video/x-msvideo": [],
      "video/x-ms-wmv": [],
      "video/x-flv": [],
      "video/3gpp": [],
      "video/x-matroska": [],
      "video/x-m4v": [],
      "video/webm": [],
    },
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        alert("Only video files are allowed!");
        return;
      }
      const file = acceptedFiles[0];
      if (file.size > MAX_FILE_SIZE) {
        alert("File size exceeds the 5GB limit!");
        return;
      }
      setSelectedFile(file);
    },
  });

  if (videoInfrastructure !== "IZLESENE") {
    return <UploadVideoAWS />;
  }

  return (
    <Page title="Upload Video">
      <div className="flex justify-center mb-4">
        <button
          style={{ outline: "none" }}
          onFocus={(e) => (e.target.style.boxShadow = "none")}
          className={`px-4 py-2 border-b-2 ${
            activeTab === "local"
              ? "border-blue-500 font-bold"
              : "border-gray-300"
          }`}
          onClick={() => setActiveTab("local")}
        >
          Upload from Local Storage
        </button>
        <button
          style={{ outline: "none" }}
          onFocus={(e) => (e.target.style.boxShadow = "none")}
          className={`px-4 py-2 border-b-2 ${
            activeTab === "link"
              ? "border-blue-500 font-bold"
              : "border-gray-300"
          }`}
          onClick={() => setActiveTab("link")}
        >
          Upload via URL
        </button>
      </div>
      {activeTab === "local" ? (
        <Card className="max-w-lg m-auto">
          <Card.Body>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed p-6 text-center cursor-pointer transition-all duration-300 ${
                isDragActive
                  ? "border-blue-500 bg-blue-100 shadow-lg"
                  : selectedFile
                  ? "border-blue-500 bg-blue-100 shadow-lg"
                  : "border-gray-300 bg-white"
              }`}
            >
              <input {...getInputProps()} />
              {selectedFile ? (
                <p className="text-empower-500">
                  Selected: {selectedFile.name}
                </p>
              ) : (
                <p>Drag & Drop a video file here, or click to select</p>
              )}
            </div>

            <div className="flex justify-end mt-4">
              <Button
                color="primary"
                icon={faCloudUploadAlt}
                disabled={!selectedFile}
                loading={loading}
                onClick={onSubmit}
              >
                <Translation>
                  {(t) => capitalize(t("upload video"))}
                </Translation>
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card className="max-w-lg m-auto">
          <Card.Body>
            <Form onSubmit={onSubmit}>
              <Form.FormTextInput
                autoFocus={true}
                name="source"
                label="Video URL"
                placeholder="https://www.youtube.com/watch?v=1A2B3C4D"
                onChange={onChange}
                value={values.source}
              />
              <div className="flex justify-end">
                <Button
                  color="primary"
                  disabled={!values.source.length || loading}
                  loading={loading}
                  onClick={onSubmit}
                  icon={faCloudUploadAlt}
                >
                  <Translation>
                    {(t) => capitalize(t("upload video"))}
                  </Translation>
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
      {showModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50"
          style={{ zIndex: 999 }}
        >
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md text-center">
            <h3 className="text-lg font-bold text-gray-900">Upload Status</h3>
            <p className="mt-2 text-gray-700">
              The first step of uploading your video has been completed. After
              further processing, you will be able to view the video on /videos.
              <br />
              This can take up to 10 minutes depending on the size of your
              video.
            </p>
            <div className="mt-4 text-right flex justify-end">
              <Button color={"inverse_primary"} onClick={handleModalClose}>
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "local" && loading && (
        <div className="mx-auto w-full md:w-1/2 mt-6 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 text-center text-md">
          <div className="mt-2 text-center font-bold">
            <p>Uploading: {progress}%</p>
            <div className="h-2 bg-gray-200 rounded-full my-3">
              <div
                className="h-2 bg-empower-500 rounded-full"
                style={{ width: `${progress}%` }}
              ></div>
            </div>

            <p>
              Your video is currently being uploaded. Please do not close this
              tab. You will be notified when this process ends.
            </p>
          </div>
        </div>
      )}

      {activeTab === "local" ? (
        <div className="mx-auto w-full md:w-1/2 mt-6 p-4 bg-orange-100 border-l-4 border-orange-500 text-orange-700 text-center text-sm">
          <ul className="list-disc list-inside">
            <li>
              <strong>Supported formats:</strong> *.3gp, *.aac, *.asf, *.avi,
              *.divx, *.dv, *.flv, *.m4a, *.m4v, *.mkv, *.mov, *.mp3, *.mp4,
              *.mpeg, *.mpg, *.qt, *.vob, *.wmv
            </li>
            <li>
              <strong>Max File Size:</strong> 5GB
            </li>
            <li>
              After upload, your video will be queued for processing. It can
              take up to <strong>10 minutes</strong>. You can follow the status
              of your video on the <em>/videos</em> page.
            </li>
          </ul>
        </div>
      ) : (
        <div className="mx-auto w-full md:w-1/2 mt-6 p-4 bg-orange-100 border-l-4 border-orange-500 text-orange-700 text-center text-sm">
          <ul className="list-disc list-inside">
            <li>
              <strong>Supported platforms:</strong> youtube & dailymotion.
            </li>

            <li>
              After upload, your video will be queued for processing. It can
              take up to <strong>10 minutes</strong>. You can follow the status
              of your video on the <em>/videos</em> page.
            </li>
          </ul>
        </div>
      )}
    </Page>
  );
};

export default UploadVideo;
